import React, { useState, useEffect } from 'react';
import axiosInstance from '../../services/api/axiosInstance';

const ActivityContractSelector = ({ 
  selectedContracts = [],
  onContractChange,
  teamId,
  className = "",
  placeholder = "Select smart contracts",
  showLabel = true,
  labelText = "Smart Contracts",
  selectFirstOnly = false // New prop to control auto-selection behavior
}) => {
  const [contractArray, setContractArray] = useState([]);
  const [isLoadingContracts, setIsLoadingContracts] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Fetch contracts for the team
  useEffect(() => {
    const fetchContracts = async () => {
      if (!teamId) {
        console.log("ActivityContractSelector: No teamId provided");
        return;
      }
      
      try {
        setIsLoadingContracts(true);
        // Backend expects team name, not ID - get team name from localStorage
        let teamName = teamId;
        if (typeof teamId === 'number' || !isNaN(teamId)) {
          // If teamId is a number, get team name from localStorage
          teamName = localStorage.getItem('selectedTeam') || teamId;
        }
        console.log("ActivityContractSelector: Fetching contracts for team:", teamName);
        
        // Backend route is /api/smart-contracts/team/:teamName
        const response = await axiosInstance.get(`/smart-contracts/team/${encodeURIComponent(teamName)}`);
        
        console.log("ActivityContractSelector: API response:", response.data);
        
        if (response.data && response.data.contracts) {
          const contracts = response.data.contracts.map(contract => ({
            id: contract.contractId || contract.id,
            address: contract.address || contract.contract_address,
            name: contract.name || contract.contract_name || `Contract ${(contract.address || contract.contract_address || '').substring(0, 6)}...${(contract.address || contract.contract_address || '').substring((contract.address || contract.contract_address || '').length - 4)}`,
            blockchain: contract.blockchain || contract.chain,
            tokenSymbol: contract.tokenSymbol || contract.symbol || contract.contract_symbol,
            contractType: contract.contractType || 'main'
          }));
          
          console.log("ActivityContractSelector: Mapped contracts:", contracts);
          setContractArray(contracts);
          
          // Auto-select contracts if none selected
          if (selectedContracts.length === 0 && contracts.length > 0) {
            if (selectFirstOnly) {
              // Select only the first contract
              onContractChange([contracts[0]]);
            } else {
              // Select all contracts (legacy behavior)
              onContractChange(contracts);
            }
          }
        } else {
          console.warn("ActivityContractSelector: No contracts in response:", response.data);
          setContractArray([]);
        }
      } catch (error) {
        console.error("ActivityContractSelector: Error fetching contracts:", error);
        console.error("ActivityContractSelector: Error response:", error.response?.data);
        console.error("ActivityContractSelector: Error status:", error.response?.status);
        setContractArray([]); // Set empty array on error
      } finally {
        setIsLoadingContracts(false);
      }
    };

    fetchContracts();
  }, [teamId]);

  const toggle = () => setIsOpen(!isOpen);

  const onSelect = (contract) => {
    const isSelected = (selectedContracts || []).some(c => c.id === contract.id);
    let updated = [];
    
    if (isSelected) {
      updated = (selectedContracts || []).filter(c => c.id !== contract.id);
    } else {
      updated = [...(selectedContracts || []), contract];
    }
    
    if (onContractChange) {
      onContractChange(updated);
    }
  };

  const handleSelectAll = () => {
    if (selectedContracts.length === contractArray.length) {
      // Deselect all
      onContractChange([]);
    } else {
      // Select all
      onContractChange(contractArray);
    }
  };

  const getDisplayText = () => {
    if (selectedContracts.length === 0) {
      return placeholder;
    } else if (selectedContracts.length === 1) {
      return selectedContracts[0].name;
    } else if (selectedContracts.length === contractArray.length) {
      return 'All smart contracts';
    } else {
      return `${selectedContracts.length} smart contracts selected`;
    }
  };

  return (
    <div className={`relative ${className}`}>
      {showLabel && (
        <label className="block text-xs font-medium mb-2 text-[#475569]">
          {labelText}
        </label>
      )}
      
      <div className="relative">
        <button
          type="button"
          onClick={toggle}
          className="w-full px-3 py-2 text-sm bg-white/60 backdrop-blur-sm border border-gray-300 hover:bg-white/80 transition-all duration-200 flex items-center justify-between min-w-[180px] focus:outline-none focus:ring-2 focus:ring-[#1d0c46] focus:border-[#1d0c46]"
          disabled={isLoadingContracts}
        >
          <div className="flex items-center gap-2">
            {isLoadingContracts ? (
              <>
                <div className="w-2 h-2 bg-gray-400 animate-pulse"></div>
                <span className="text-[#475569]">Loading...</span>
              </>
            ) : (
              <span className="text-[#475569] truncate">
                {getDisplayText()}
              </span>
            )}
          </div>
          
          <svg 
            className={`w-4 h-4 text-[#475569] transition-transform duration-200 ml-auto ${isOpen ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-[9999] w-full mt-1 bg-white border border-gray-200 max-h-60 overflow-auto shadow-xl" style={{ top: '100%' }}>
            {isLoadingContracts ? (
              <div className="px-3 py-2 text-sm text-[#475569]">
                Loading contracts...
              </div>
            ) : contractArray.length === 0 ? (
              <div className="px-3 py-2 text-sm text-[#475569]">
                No smart contracts found
              </div>
            ) : (
              <>
                {/* Select All option */}
                <button
                  onClick={handleSelectAll}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 border-b border-gray-100 ${
                    selectedContracts.length === contractArray.length ? 'bg-[#1d0c46]/10 text-[#1d0c46]' : 'text-[#475569]'
                  }`}
                >
                  <div className={`w-4 h-4 border-2 flex items-center justify-center ${
                    selectedContracts.length === contractArray.length ? 'bg-[#1d0c46] border-[#1d0c46]' : 'border-gray-300'
                  }`}>
                    {selectedContracts.length === contractArray.length && (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <span className="font-medium">Select All</span>
                </button>

                {/* Individual contract options */}
                {contractArray.map((contract) => (
                  <button
                    key={contract.id}
                    onClick={() => onSelect(contract)}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                      selectedContracts.some(c => c.id === contract.id) ? 'bg-[#1d0c46]/10 text-[#1d0c46]' : 'text-[#475569]'
                    }`}
                  >
                    <div className={`w-4 h-4 border-2 flex items-center justify-center ${
                      selectedContracts.some(c => c.id === contract.id) ? 'bg-[#1d0c46] border-[#1d0c46]' : 'border-gray-300'
                    }`}>
                      {selectedContracts.some(c => c.id === contract.id) && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <span className="truncate">{contract.name}</span>
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {/* Click outside to close */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-[9998]" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default ActivityContractSelector;

